import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader, StatCards, Card, TabBar, Table, StatusPill, Field, inputStyle, btnPrimary, Empty, fmtKES, btnSm , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { printPaymentRequestSlip, printCashierReceipt } from '../utils/print';
import ComplimentaryModal from '../components/ComplimentaryModal';
import { playReadyAlert, unlockAudio } from '../hooks/useOrderSound';

// ─── Kitchen status pipeline display ─────────────────────────────────────────
const KS_STEPS = ['pending', 'preparing', 'ready', 'served'];
const KS_META  = {
  pending:   { label: 'Pending',    icon: '🔴', color: '#e74c3c' },
  preparing: { label: 'Preparing',  icon: '🟡', color: '#e67e22' },
  ready:     { label: 'Ready!',     icon: '🟢', color: '#2ecc71' },
  served:    { label: 'Served',     icon: '✅', color: '#3498db' },
};

export default function WaiterPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab] = useState('order');
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const { user } = useAuth();

  // Track order IDs that were already 'ready' so we only beep on NEW ready orders
  const prevReadyIds = useRef(new Set());
  const isFirstLoad  = useRef(true);

  const loadAll = useCallback(async () => {
    const [o, b, p] = await Promise.all([api.getOrders(), api.getBills(), api.getPayments()]);
    const allOrders = o.data.results || o.data;

    // Only watch THIS waiter's own orders for ready status
    const myOrders = allOrders.filter(order =>
      !order.is_voided && order.waiter_name === user?.name
    );
    const nowReady = myOrders.filter(order => order.kitchen_status === 'ready');
    const newReady = nowReady.filter(order => !prevReadyIds.current.has(order.id));

    if (!isFirstLoad.current && newReady.length > 0) {
      playReadyAlert();
      notify(`🍽️ ${newReady.length} order${newReady.length > 1 ? 's' : ''} ready to collect!`);
    }

    prevReadyIds.current = new Set(nowReady.map(o => o.id));
    isFirstLoad.current  = false;

    setOrders(allOrders);
    setBills(b.data.results || b.data);
    setPayments(p.data.results || p.data);
  }, [user]);

  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 10000);
    return () => clearInterval(iv);
  }, [loadAll]);

  const myPending    = bills.filter(b => b.status === 'pending' || b.status === 'payment-requested');
  const myActive     = orders.filter(o => !o.is_voided && ['pending','preparing','ready'].includes(o.kitchen_status));
  const todayRevenue = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  return (
    <div onClick={unlockAudio}>
      <PageHeader icon="🍽️" title={`Waiter — ${user?.name}`} sub="Place orders, track kitchen progress & manage bills" />
      <StatCards stats={[
        { label:"Today's Orders",   value:orders.filter(o=>o.created_at?.startsWith(new Date().toISOString().slice(0,10))).length, color:'#3498db', icon:'📝' },
        { label:'Active in Kitchen',value:myActive.length,      color:myActive.some(o=>o.kitchen_status==='ready')?'#2ecc71':'#e67e22', icon:'🍳', sub: myActive.some(o=>o.kitchen_status==='ready') ? '🟢 Items ready!' : 'Preparing' },
        { label:'Pending Bills',    value:myPending.length,     color:'#e74c3c', icon:'🧾' },
        { label:"Today's Revenue",  value:fmtKES(todayRevenue), color:'var(--accent)', icon:'💰' },
      ]} />
      <TabBar tabs={[
        { id: 'order',    label: 'New Order',          icon: '📝' },
        { id: 'tracking', label: `Kitchen (${myActive.length})`, icon: myActive.some(o=>o.kitchen_status==='ready') ? '🟢' : '🍳' },
        { id: 'mybills',  label: 'My Bills',            icon: '🧾' },
        { id: 'history',  label: 'My History',          icon: '📋' },
      ]} active={tab} onChange={setTab} />
      {tab === 'order'    && <WaiterOrderPanel notify={notify} onOrderPlaced={() => { loadAll(); setTab('tracking'); }} />}
      {tab === 'tracking' && <WaiterOrderTracking orders={myActive} onRefresh={loadAll} />}
      {tab === 'mybills'  && <WaiterBills bills={myPending} notify={notify} onUpdate={loadAll} />}
      {tab === 'history'  && <WaiterHistory orders={orders} payments={payments} bills={bills} notify={notify} onUpdate={loadAll} />}
    </div>
  );
}


// ─── Order Tracking Panel ─────────────────────────────────────────────────────
function WaiterOrderTracking({ orders, onRefresh }) {
  if (orders.length === 0) {
    return (
      <Card title="Kitchen Tracker">
        <Empty text="No active orders in the kitchen. Place a new order to track it here." />
      </Card>
    );
  }

  return (
    <Card title={`Active Orders in Kitchen (${orders.length})`}>
      <div style={{ display: 'grid', gap: 14 }}>
        {[...orders].sort((a,b) => {
          // Sort: ready first, then preparing, then pending
          const pri = { ready: 0, preparing: 1, pending: 2 };
          return (pri[a.kitchen_status]||3) - (pri[b.kitchen_status]||3);
        }).map(order => {
          const stepIdx  = KS_STEPS.indexOf(order.kitchen_status);
          const meta     = KS_META[order.kitchen_status] || KS_META.pending;
          const isReady  = order.kitchen_status === 'ready';

          return (
            <div key={order.id} style={{
              background: isReady ? '#0a1a0a' : 'var(--bgInput)',
              border: `2px solid ${isReady ? '#2ecc71' : meta.color + '55'}`,
              borderRadius: 6,
              padding: 16,
              animation: isReady ? 'pulse 1.5s infinite' : 'none',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <span style={{ fontWeight: 700, color:'var(--textPrimary)', fontSize: 15 }}>Order #{order.id}</span>
                  <span style={{ marginLeft: 10, fontSize: 12, color:'var(--textMuted)' }}>Table {order.table_number}</span>
                  {order.kitchen_station && <span style={{ marginLeft: 8, fontSize: 11, background: '#1a1a0a', border: '1px solid #c8a96e44', borderRadius: 3, padding: '2px 7px', color: 'var(--accent)' }}>📍 {order.kitchen_station}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{meta.icon}</span>
                  <span style={{ fontWeight: 700, color: meta.color, fontSize: 13 }}>{meta.label}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                {KS_STEPS.filter(s => s !== 'served').map((step, i) => {
                  const done    = i <= stepIdx;
                  const current = i === stepIdx;
                  const sm      = KS_META[step];
                  return (
                    <div key={step} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        height: 6, borderRadius: 3,
                        background: done ? sm.color : '#2a2a2a',
                        marginBottom: 4,
                        transition: 'background .4s',
                      }} />
                      <div style={{ fontSize: 9, color: current ? sm.color : done ? '#555' : '#333', fontWeight: current ? 700 : 400, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {sm.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Items */}
              <div style={{ background: '#0e0e0e', borderRadius: 3, padding: '8px 10px', marginBottom: 8 }}>
                {order.items?.map(i => (
                  <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
                    <span style={{ color:'var(--textSecond)' }}>×{i.quantity} {i.name}</span>
                    <span style={{ color:'var(--textMuted)' }}>{fmtKES(parseFloat(i.price) * i.quantity)}</span>
                  </div>
                ))}
                {order.note && <div style={{ color: 'var(--accent)', fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>📝 {order.note}</div>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color:'var(--textFaint)' }}>Ordered at {new Date(order.created_at).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}</span>
                {isReady && (
                  <div style={{ background: '#2ecc71', color: '#111', fontWeight: 700, fontSize: 12, padding: '6px 14px', borderRadius: 4, letterSpacing: 1 }}>
                    🍽️ READY TO COLLECT & SERVE
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes pulse { 0%,100%{ box-shadow: 0 0 0 0 rgba(46,204,113,0.4); } 50%{ box-shadow: 0 0 0 8px rgba(46,204,113,0); } }`}</style>
    </Card>
  );
}


// ─── New Order Panel ──────────────────────────────────────────────────────────
function WaiterOrderPanel({ notify, onOrderPlaced }) {
  const bp = useBreakpoint();
  const [src, setSrc]         = useState('food');
  const [menu, setMenu]       = useState([]);
  const [cart, setCart]       = useState([]);
  const [table, setTable]     = useState('');
  const [note, setNote]       = useState('');
  const [guestId, setGuestId] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [guests, setGuests]   = useState([]);
  const [cat, setCat]         = useState('All');
  const [loading, setLoading] = useState(false);
  const [station, setStation] = useState('');
  const [stations, setStations] = useState([]);

  useEffect(() => {
    api.getMenu(src).then(r => { setMenu(r.data.results || r.data); setCat('All'); });
    api.getGuests().then(r => setGuests((r.data.results || r.data).filter(g => g.status === 'checked-in')));
    // Load available kitchen stations from staff + always include defaults
    api.getUsers().then(r => {
      const users = r.data.results || r.data;
      const fromStaff = [...new Set(
        users.filter(u => ['kitchen','chef'].includes(u.role) && u.kitchen_station)
             .map(u => u.kitchen_station)
      )];
      // Always show at least these default stations
      const defaults = ['Main Kitchen', 'Bakery', 'Grill Station'];
      const allStations = [...new Set([...fromStaff, ...defaults.filter(d => !fromStaff.some(s => s.toLowerCase() === d.toLowerCase()))])];
      setStations(allStations);
    }).catch(() => {
      // Fallback if users API unavailable
      setStations(['Main Kitchen', 'Bakery', 'Grill Station']);
    });
  }, [src]);

  const cats  = ['All', ...new Set(menu.map(m => m.category))];
  const shown = cat === 'All' ? menu.filter(m => m.is_active) : menu.filter(m => m.is_active && m.category === cat);
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const addItem = (item) => setCart(c => { const ex = c.find(i => i.id === item.id); return ex ? c.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i) : [...c, { ...item, qty: 1 }]; });
  const remItem = (id)   => setCart(c => c.filter(i => i.id !== id));
  const adjQty  = (id,d) => setCart(c => c.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));

  const send = async () => {
    if (cart.length === 0) { notify('Cart is empty', 'error'); return; }
    if (!table) { notify('Enter table / seat number', 'error'); return; }
    if (src === 'food' && stations.length > 0 && !station) { notify('Please select a kitchen station', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.createOrder({
        order_type: src,
        table_number: table,
        guest: guestId || null,
        note,
        payment_mode: payMode,
        kitchen_station: src === 'food' ? station : '',
        items: cart.map(i => ({ id: i.id, quantity: i.qty })),
      });
      // Print payment request slip automatically
      const orderData = res.data;
      printPaymentRequestSlip({
        id: orderData.id,
        table_number: orderData.table_number,
        guest_name: orderData.guest_name || 'Walk-in',
        waiter_name: orderData.waiter_name,
        kitchen_station: station,
        order_type: src,
        items: cart.map(i => ({ name: i.name, quantity: i.qty, price: i.price, subtotal: i.price * i.qty })),
        total,
        note,
      });
      setCart([]); setTable(''); setNote(''); setGuestId(''); setStation('');
      onOrderPlaced();
      notify(src === 'food' ? `✅ Order sent to ${station || 'kitchen'}! Payment slip printed.` : '✅ Bar bill created! Payment slip printed.');
    } catch (e) {
      notify('Failed to place order', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 340px', gap: 16 }}>
      <Card title="Menu">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[['food', '🍽️ Restaurant'], ['bar', '🍹 Bar']].map(([v, lbl]) => (
            <button key={v} style={{ background: src===v?'#222':'var(--bgInput)', border: `1px solid ${src===v?'#c8a96e':'var(--borderMid)'}`, borderRadius: 4, cursor: 'pointer', padding: '7px 16px', color: src===v?'#c8a96e':'#777', fontSize: 13, fontFamily: 'inherit' }} onClick={() => { setSrc(v); setCart([]); }}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {cats.map(c => <button key={c} style={{ background: cat===c?'#222':'var(--bgInput)', border: `1px solid ${cat===c?'#c8a96e':'var(--borderMid)'}`, borderRadius: 3, padding: '4px 12px', color: cat===c?'#c8a96e':'#777', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => setCat(c)}>{c}</button>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10 }}>
          {shown.map(item => (
            <div key={item.id} style={{ background: 'var(--bgInput)', border: '1px solid #242424', borderRadius: 4, padding: 12, cursor: 'pointer' }} onClick={() => addItem(item)}>
              <div style={{ fontWeight: 700, fontSize: 13, color:'var(--textPrimary)', marginBottom: 4 }}>{item.name}</div>
              <div style={{ fontSize: 11, color:'var(--textFaint)', marginBottom: 6 }}>{item.category}</div>
              <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{fmtKES(item.price)}</div>
              <div style={{ background: 'var(--accent)', color: '#111', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 3, display: 'inline-block' }}>＋ Add</div>
            </div>
          ))}
        </div>
      </Card>

      <div>
        <Card title="Order Details">
          <Field label="Table / Seat *">
            <input style={inputStyle} value={table} onChange={e => setTable(e.target.value)} placeholder="e.g. T-05" />
          </Field>

          {/* Kitchen station selector — MANDATORY for food orders */}
          {src === 'food' && stations.length > 0 && (
            <Field label="Kitchen Station *">
              <div style={{ position:'relative' }}>
                <select
                  style={{
                    ...inputStyle,
                    border: !station ? '2px solid #e67e22' : '1px solid #c8a96e',
                    background: !station ? '#1a0e00' : inputStyle.background,
                    fontWeight: !station ? 600 : 400,
                  }}
                  value={station}
                  onChange={e => setStation(e.target.value)}
                >
                  <option value="">— ⚠ Select Kitchen Station (Required) —</option>
                  {stations.map(s => {
                    const icon = s.toLowerCase().includes('bak') ? '🥐' : s.toLowerCase().includes('grill') ? '🔥' : s.toLowerCase().includes('pool') ? '🏊' : s.toLowerCase().includes('bar') ? '🍹' : s.toLowerCase().includes('pastry') ? '🧁' : '👨‍🍳';
                    return <option key={s} value={s}>{icon} {s}</option>;
                  })}
                </select>
              </div>
              {!station && (
                <div style={{ marginTop:5, padding:'8px 10px', background:'#1a0e00', border:'1px solid #e67e22', borderRadius:3, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:16 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize:12, color:'#e67e22', fontWeight:700 }}>Kitchen station is required</div>
                    <div style={{ fontSize:11, color:'#aa7a33' }}>Select which kitchen will prepare this order before submitting.</div>
                  </div>
                </div>
              )}
              {station && (
                <div style={{ marginTop:4, fontSize:11, color:'#2ecc71' }}>✓ Will be sent to: <strong>{station}</strong></div>
              )}
            </Field>
          )}
          {src === 'food' && stations.length === 0 && (
            <Field label="Kitchen Station *">
              <input style={{ ...inputStyle, border:'2px solid #e67e22' }} value={station} onChange={e => setStation(e.target.value)} placeholder="e.g. Main Kitchen — required" />
              {!station && <div style={{ fontSize:11, color:'#e67e22', marginTop:3 }}>⚠ Enter the kitchen station name</div>}
            </Field>
          )}

          <Field label="Link to Hotel Guest">
            <select style={inputStyle} value={guestId} onChange={e => { setGuestId(e.target.value); if (e.target.value) setPayMode('room-charge'); else setPayMode('cash'); }}>
              <option value="">— Walk-in / Table —</option>
              {guests.map(g => <option key={g.id} value={g.id}>{g.name} (Rm {g.room_number})</option>)}
            </select>
          </Field>

          {guestId && (
            <div style={{ background: '#1a1a0a', border: '1px solid #c8a96e44', borderRadius: 3, padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
              <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>🏨 Billing Mode</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1, padding: '8px 10px', background: payMode==='room-charge'?'#2a1a00':'#111', border: `1px solid ${payMode==='room-charge'?'#c8a96e':'var(--borderMid)'}`, borderRadius: 3 }}>
                  <input type="radio" value="room-charge" checked={payMode==='room-charge'} onChange={() => setPayMode('room-charge')} style={{ accentColor: '#c8a96e' }} />
                  <span style={{ fontSize: 12, color: payMode==='room-charge'?'#c8a96e':'#888' }}>🏨 Charge to Room</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1, padding: '8px 10px', background: payMode==='cash'?'#0a1a0a':'#111', border: `1px solid ${payMode==='cash'?'#2ecc71':'var(--borderMid)'}`, borderRadius: 3 }}>
                  <input type="radio" value="cash" checked={payMode==='cash'} onChange={() => setPayMode('cash')} style={{ accentColor: '#2ecc71' }} />
                  <span style={{ fontSize: 12, color: payMode==='cash'?'#2ecc71':'#888' }}>💵 Pay Separately</span>
                </label>
              </div>
              {payMode === 'room-charge' && <div style={{ fontSize: 11, color:'var(--textMuted)', marginTop: 6 }}>Bill posted to guest folio — settled at checkout.</div>}
            </div>
          )}

          <Field label="Kitchen Note">
            <textarea style={{ ...inputStyle, height: 52, resize: 'none' }} value={note} onChange={e => setNote(e.target.value)} placeholder="Allergies, special requests…" />
          </Field>

          {/* Cart */}
          <div style={{ background: '#0e0e0e', border: '1px solid #1e1e1e', borderRadius: 3, padding: 10, minHeight: 80, marginBottom: 10 }}>
            <div style={{ fontSize: 10, color:'var(--textFaint)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
              Cart {cart.length > 0 && <span style={{ background: 'var(--accent)', color: '#111', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700, marginLeft: 6 }}>{cart.length}</span>}
            </div>
            {cart.length === 0 ? <div style={{ color:'var(--textFaint)', fontSize: 13, padding: '10px 0' }}>No items yet</div> :
              cart.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #1a1a1a' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color:'var(--textPrimary)' }}>{item.name}</div>
                    <div style={{ fontSize: 11, color:'var(--textMuted)' }}>{fmtKES(item.price)} each</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button style={{ background: '#222', border: 'none', borderRadius: 2, width: 22, height: 22, color:'var(--textMuted)', cursor: 'pointer', fontSize: 14 }} onClick={() => adjQty(item.id,-1)}>−</button>
                    <span style={{ fontSize: 14, fontWeight: 700, color:'var(--textPrimary)', minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                    <button style={{ background: '#222', border: 'none', borderRadius: 2, width: 22, height: 22, color:'var(--textMuted)', cursor: 'pointer', fontSize: 14 }} onClick={() => adjQty(item.id,1)}>+</button>
                  </div>
                  <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: 70, textAlign: 'right' }}>{fmtKES(item.price * item.qty)}</span>
                  <button style={{ background:'var(--bgDanger)', border: 'none', borderRadius: 2, width: 22, height: 22, color:'var(--textMuted)', cursor: 'pointer', fontSize: 11 }} onClick={() => remItem(item.id)}>✕</button>
                </div>
              ))
            }
          </div>

          {cart.length > 0 && <div style={{ textAlign: 'right', fontWeight: 600, fontSize: 15, marginBottom: 8, color:'var(--textPrimary)' }}>Total: <span style={{ color: 'var(--accent)' }}>{fmtKES(total)}</span></div>}

          <button
            style={{
              ...btnPrimary,
              ...(src === 'food' && !station ? { background:'#3a2000', border:'1px solid #e67e22', color:'#e67e22', cursor:'not-allowed', opacity:0.7 } : {}),
            }}
            onClick={send}
            disabled={loading || (src === 'food' && !station)}
          >
            {loading ? 'Sending…' : src === 'food' && !station ? '⚠ Select Kitchen Station First' : payMode==='room-charge' && guestId ? '🏨 Post to Room & Print Slip' : src==='food' ? '📤 Send to Kitchen & Print Slip' : '✓ Create Bar Bill & Print Slip'}
          </button>

          <div style={{ marginTop: 8, fontSize: 11, color:'var(--textFaint)', textAlign: 'center' }}>
            A payment request slip will print automatically. Official receipt issued by cashier after payment.
          </div>
        </Card>
      </div>
    </div>
  );
}


// ─── Payment Request Modal ────────────────────────────────────────────────────
const MODE_INFO = {
  cash:          { label: 'Cash',          icon: '💵', hint: 'No transaction code needed',                                                color: '#2ecc71' },
  mpesa:         { label: 'M-Pesa',        icon: '📱', hint: 'Enter the M-Pesa confirmation code from the customer SMS',                  color: '#00b300' },
  bank:          { label: 'Bank Transfer', icon: '🏦', hint: 'Enter the bank transaction reference number',                               color: '#3498db' },
  pdq:           { label: 'PDQ / Card',    icon: '💳', hint: 'Enter the authorization code printed on the PDQ receipt',                   color: '#9b59b6' },
  'room-charge': { label: 'Room Charge',   icon: '🏨', hint: 'Charge directly to the guest room account',                                color: '#e67e22' },
  complimentary: { label: 'Complimentary', icon: '🎁', hint: 'Waive this bill — requires manager approval before it is cleared',          color: 'var(--accent)' },
};

const MODE_INFO_EXT = {
  ...MODE_INFO,
  stripe: { label: 'Stripe', icon: '💙', hint: 'Stripe online card payment', color: '#635bff' },
};

function PaymentRequestModal({ bill, onClose, onConfirm, notify }) {
  const [mode, setMode]         = useState('cash');
  const [txCode, setTxCode]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [stkLoading, setStkLoading] = useState(false);
  const [showComp, setShowComp] = useState(false);
  const needsCode = ['mpesa','bank','pdq','stripe'].includes(mode);
  const info = MODE_INFO_EXT[mode] || MODE_INFO[mode];

  const triggerMpesaSTK = async () => {
    const phone = prompt('Enter customer M-Pesa phone (254XXXXXXXXX):');
    if (!phone) return;
    setStkLoading(true);
    try {
      const r = await api.mpesaStkPush({ phone, amount: Math.ceil(parseFloat(bill.total) || 100), description: `Bill #${bill.id}` });
      setTxCode(r.data.CheckoutRequestID || r.data.MerchantRequestID || 'sent');
      setMode('mpesa');
      alert(`✅ STK Push sent to ${phone}!\nAsk customer to enter their M-Pesa PIN.`);
    } catch(e) {
      alert('M-Pesa STK Push failed: ' + (e.response?.data?.error || e.message));
    } finally { setStkLoading(false); }
  };

  const submit = async () => {
    if (mode === 'complimentary') { setShowComp(true); return; }
    if (needsCode && !txCode.trim()) return;
    setLoading(true);
    try { await onConfirm(bill.id, mode, txCode.trim()); onClose(); } catch {}
    finally { setLoading(false); }
  };

  if (showComp) return (
    <ComplimentaryModal
      amount={bill.total}
      guestName={bill.guest_name || ''}
      billId={bill.id}
      notify={notify}
      onClose={() => setShowComp(false)}
      onSubmitted={() => { onClose(); }}
    />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bgModal)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bgCard)', border:'1px solid var(--border)', borderRadius: 6, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>Request Payment</div>
            <div style={{ fontSize: 12, color:'var(--textMuted)' }}>Bill #{bill.id} · Table {bill.order_table || bill.order} · {bill.bill_type}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color:'var(--textFaint)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ background: '#0e0e0e', borderRadius: 4, padding: '10px 12px', marginBottom: 20 }}>
          {(bill.order_items||[]).map(i => <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}><span style={{ color:'var(--textSecond)' }}>{i.name} ×{i.quantity}</span><span style={{ color:'var(--textMuted)' }}>{fmtKES(parseFloat(i.price)*i.quantity)}</span></div>)}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 6, borderTop: '1px solid #222', fontWeight: 700 }}><span style={{ color:'var(--textPrimary)' }}>TOTAL</span><span style={{ color: 'var(--accent)', fontSize: 18 }}>{fmtKES(bill.total)}</span></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={triggerMpesaSTK} disabled={stkLoading} style={{ background: '#00b300', border: 'none', borderRadius: 3, padding: '8px 14px', color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>{stkLoading ? '⏳ Sending…' : '📱 M-Pesa STK Push'}</button>
        </div>
        <div style={{ fontSize: 10, color:'var(--textFaint)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Select Payment Method</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(100px,1fr))', gap: 6, marginBottom: 14 }}>
          {Object.entries(MODE_INFO).map(([key, mi]) => (
            <button key={key} onClick={() => { setMode(key); setTxCode(''); }} style={{ background: mode===key?'var(--border)':'#111', border: `2px solid ${mode===key?mi.color:'var(--borderMid)'}`, borderRadius: 6, padding: '10px 4px', cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 18, marginBottom: 3 }}>{mi.icon}</div>
              <div style={{ fontSize: 9, fontWeight: 600, color: mode===key?mi.color:'var(--textFaint)', lineHeight: 1.2 }}>{mi.label}</div>
            </button>
          ))}
        </div>
        <div style={{ background: '#0e0e0e', border: `1px solid ${info.color}33`, borderRadius: 4, padding: '8px 12px', marginBottom: 14, fontSize: 12, color:'var(--textMuted)' }}>ℹ️ {info.hint}</div>
        {needsCode && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color:'var(--textFaint)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
              {mode==='mpesa'?'M-Pesa Confirmation Code *':mode==='bank'?'Bank Ref *':'PDQ Auth Code *'}
            </div>
            <input style={{ ...inputStyle, border: `1px solid ${txCode?info.color:'var(--borderMid)'}`, fontSize: 16, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, color: info.color }}
              value={txCode} onChange={e => setTxCode(e.target.value.toUpperCase())} autoFocus
              placeholder={mode==='mpesa'?'e.g. QHX7Y3K2AB':mode==='bank'?'e.g. TXN20240112':'e.g. AUTH123456'}
            />
            {!txCode.trim() && <div style={{ color: '#e74c3c', fontSize: 11, marginTop: 4 }}>⚠ Transaction code is required</div>}
          </div>
        )}
        <button style={{ ...btnPrimary, background: info.color, color: mode==='complimentary'?'#111':'#111', opacity: (needsCode&&!txCode.trim())?0.4:1 }} onClick={submit} disabled={loading||(needsCode&&!txCode.trim())}>
          {loading ? 'Submitting…' : mode==='complimentary' ? '🎁 Submit Complimentary Request' : '💳 Submit Payment Request to Cashier'}
        </button>
        <div style={{ marginTop: 10, fontSize: 11, color:'var(--textFaint)', textAlign: 'center' }}>
          {mode==='complimentary' ? 'Manager approval required before this bill is cleared.' : 'Cashier will confirm payment and issue the official receipt to the customer.'}
        </div>
      </div>
    </div>
  );
}


// ─── My Bills Panel ───────────────────────────────────────────────────────────
function WaiterBills({ bills, notify, onUpdate }) {
  const [activeModal, setActiveModal] = useState(null);

  const requestPay = async (billId, mode, txCode) => {
    await api.requestPayment(billId, { payment_mode: mode, transaction_code: txCode });
    onUpdate();
    notify('Payment request sent to cashier!');
  };

  if (bills.length === 0) return <Card title="My Pending Bills"><Empty text="No pending bills — all clear!" /></Card>;

  return (
    <>
      {activeModal && <PaymentRequestModal bill={activeModal} onClose={() => setActiveModal(null)} onConfirm={requestPay} notify={notify} />}
      <Card title={`My Pending Bills (${bills.length})`}>
        <div style={{ background:'var(--bgWarning)', border: '1px solid #c8a96e33', borderRadius: 4, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--accent)' }}>
          💡 When a customer is ready to pay, click "Request Payment" — the cashier will confirm and issue the official receipt.
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          {bills.map(bill => (
            <div key={bill.id} style={{ background: 'var(--bgCard)', border: `1px solid ${bill.status==='payment-requested'?'#c8a96e':'var(--borderMid)'}`, borderLeft: `4px solid ${bill.status==='payment-requested'?'#c8a96e':'#333'}`, borderRadius: 4, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, color:'var(--textPrimary)', fontSize: 15 }}>Bill #{bill.id}</div>
                  <div style={{ fontSize: 12, color:'var(--textMuted)', marginTop: 2 }}>Table {bill.order_table||`Order #${bill.order}`} · {bill.bill_type}</div>
                  <div style={{ fontSize: 12, color:'var(--textMuted)', marginTop: 2 }}>Guest: <strong>{bill.guest_name}</strong></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 20 }}>{fmtKES(bill.total)}</div>
                  <StatusPill status={bill.status} />
                </div>
              </div>
              <div style={{ background: '#0e0e0e', borderRadius: 3, padding: '8px 10px', marginBottom: 10 }}>
                {(bill.order_items||[]).map(i => <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1a1a1a', fontSize: 13 }}><span style={{ color:'var(--textSecond)' }}>{i.name} ×{i.quantity}</span><span style={{ color: 'var(--accent)' }}>{fmtKES(parseFloat(i.price)*i.quantity)}</span></div>)}
              </div>
              {bill.status === 'pending' && (
                <button style={{ ...btnPrimary, background: 'var(--accent)', color: '#111' }} onClick={() => setActiveModal(bill)}>
                  💳 Request Customer Payment
                </button>
              )}
              {bill.status === 'payment-requested' && (
                <div style={{ padding: '10px 14px', background: '#0e0e0e', borderRadius: 4, border:'1px solid var(--border)' }}>
                  <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>⏳ Awaiting cashier confirmation…</div>
                  <div style={{ fontSize: 12, color:'var(--textMuted)' }}>
                    Mode: <strong style={{ color:'var(--textSecond)', textTransform: 'uppercase' }}>{bill.payment_mode}</strong>
                    {bill.transaction_code && <span> · Code: <strong style={{ color: '#2ecc71', letterSpacing: 1, fontFamily: 'monospace' }}>{bill.transaction_code}</strong></span>}
                  </div>
                  <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 4 }}>Cashier will issue official receipt once confirmed.</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}


// ─── History Panel ────────────────────────────────────────────────────────────
function WaiterHistory({ orders, payments, bills }) {
  const paidBills = (bills||[]).filter(b => b.status === 'paid');
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card title={`Paid Bills — ${paidBills.length} today`}>
        {paidBills.length === 0 ? <Empty text="No paid bills yet" /> :
          <div style={{ display: 'grid', gap: 8 }}>
            {[...paidBills].reverse().map(bill => (
              <div key={bill.id} style={{ background:'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 4, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, color:'var(--textPrimary)', fontSize: 13 }}>Bill #{bill.id} — Table {bill.order_table}</div>
                  <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 2 }}>{bill.guest_name} · {bill.bill_type} · {fmtKES(bill.total)}</div>
                  <div style={{ fontSize: 10, color: '#2ecc71', marginTop: 2 }}>✓ Paid · Receipt issued by cashier</div>
                </div>
                <StatusPill status={bill.status} />
              </div>
            ))}
          </div>
        }
      </Card>

      <Card title="All My Orders">
        {orders.length === 0 ? <Empty text="No orders yet" /> :
          <Table headers={['#','Table','Station','Type','Total','Status','Time']} rows={[...orders].reverse().map(o => [
            <code style={{ fontSize: 11, color:'var(--textMuted)' }}>#{o.id}</code>,
            o.table_number,
            <span style={{ fontSize: 11, color: 'var(--accent)' }}>{o.kitchen_station || '—'}</span>,
            <span style={{ textTransform: 'capitalize', color: o.order_type==='bar'?'#8B2635':'#e67e22' }}>{o.order_type}</span>,
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{fmtKES(o.total)}</span>,
            <StatusPill status={o.kitchen_status} />,
            <span style={{ fontSize: 10, color:'var(--textFaint)' }}>{new Date(o.created_at).toLocaleTimeString('en-KE')}</span>,
          ])} />
        }
      </Card>
    </div>
  );
}